import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatRelativeTime } from '@/lib/utils'
import type { Job } from '@/types/entities'

interface JobViewDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    job: Job | null
}

export function JobViewDialog({ open, onOpenChange, job }: JobViewDialogProps) {
    if (!job) return null

    const statusColors = {
        idle: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
        running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
        paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>Job Details</DialogTitle>
                    <DialogDescription>
                        View job configuration and status
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Name</label>
                            <p className="text-sm">{job.name}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Status</label>
                            <div className="mt-1">
                                <Badge className={statusColors[job.status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'}>
                                    {job.status}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">Schedule</label>
                        <p className="text-sm font-mono bg-muted px-2 py-1 rounded">{job.schedule}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Policy</label>
                            <p className="text-sm">{job.policy?.name || `Policy #${job.policyId}`}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Target</label>
                            <p className="text-sm">{job.target?.name || `Target #${job.targetId}`}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Enabled</label>
                            <div className="mt-1">
                                <Badge variant={job.enabled ? 'default' : 'secondary'}>
                                    {job.enabled ? 'Yes' : 'No'}
                                </Badge>
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Last Run</label>
                            <p className="text-sm">{job.lastRun ? formatRelativeTime(job.lastRun) : 'Never'}</p>
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-muted-foreground">Next Run</label>
                        <p className="text-sm">{job.nextRun ? formatRelativeTime(job.nextRun) : 'Not scheduled'}</p>
                    </div>

                    {job.notes && (
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Notes</label>
                            <p className="text-sm">{job.notes}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Created</label>
                            <p className="text-sm">{formatDate(job.createdAt)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                            <p className="text-sm">{formatDate(job.updatedAt)}</p>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}



